import { useCallback, useEffect, useRef, useState } from "react";
import { getCapacity } from "../../data/marketsClient.js";
import { subscribePlanInvalidation, unsubscribePlanInvalidation } from "../../services/cacheBus.js";

// One request owner for the Markets list and its drawer. Background reads leave
// settled values mounted; failures still replace them with explicit unknowns.
export function useCapacitySnapshot(enabled) {
  const [snapshot, setSnapshot] = useState({ status: "idle" });
  const sequence = useRef(0);
  const reload = useCallback(() => {
    const request = ++sequence.current;
    setSnapshot(previous => ["idle", "loading"].includes(previous.status) ? { status: "loading" } : previous);
    return Promise.resolve().then(() => getCapacity()).then((data) => {
      if (!Array.isArray(data?.months) || !data.months.length || !Array.isArray(data?.markets)) {
        throw new Error("Capacity response was incomplete.");
      }
      if (request === sequence.current) setSnapshot(previous =>
        previous.status === "ok" && JSON.stringify(previous.data) === JSON.stringify(data)
          ? previous : { status: "ok", data });
    }).catch((error) => {
      if (request === sequence.current) setSnapshot({ status: "error", error: error?.message || "Failed to load capacity." });
    });
  }, []);
  useEffect(() => {
    if (!enabled) return;
    let deferred = false;
    const automaticRead = () => {
      if (document.visibilityState === "hidden") { deferred = true; return; }
      deferred = false;
      reload();
    };
    const onVisibility = () => {
      if (document.visibilityState !== "hidden" && deferred) automaticRead();
    };
    automaticRead();
    subscribePlanInvalidation(automaticRead);
    window.addEventListener("focus", automaticRead);
    document.addEventListener("visibilitychange", onVisibility);
    const timer = window.setInterval(automaticRead, 60000);
    return () => {
      ++sequence.current;
      unsubscribePlanInvalidation(automaticRead);
      window.removeEventListener("focus", automaticRead);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(timer);
    };
  }, [enabled, reload]);
  return [snapshot, reload];
}
